import {apiFetch} from '../../../security/apiFetch';
import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import TermTooltip from './TermTooltip';

interface HighlightedTextProps {
  text: string;
  targetLetter: string;
  foundPositions: number[];
  onLetterClick: (index: number) => void;
  token: string | null;
  showTooltips?: boolean;
  fontSize?: string | number;
  fontFamily?: string;
  direction?: 'rtl' | 'ltr';
}

interface TermMatch {
  start: number;
  end: number;
  termId: string;
  termText: string;
}

const HighlightedText: React.FC<HighlightedTextProps> = ({
  text,
  targetLetter,
  foundPositions,
  onLetterClick,
  token,
  showTooltips = true,
  fontSize = '2rem',
  fontFamily = 'Noto Naskh Arabic, serif',
  direction = 'rtl'
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | undefined>(undefined);
  const [termMatches, setTermMatches] = useState<TermMatch[]>([]);

  // Загружаем термины для текста
  React.useEffect(() => {
    let active = true;
    setTermMatches([]);
    const loadTermMatches = async () => {
      if (!showTooltips || !token) return;

      try {
        const response = await apiFetch('/api/v1/knowledge/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            q: text,
            limit: 10
          })
        });

        if (response.ok) {
          const data = await response.json();
          const matches: TermMatch[] = [];
          
          // Простой поиск терминов в тексте
          (Array.isArray(data)?data:[]).forEach((term: any) => {
            if (term.term_arabic) {
              const index = text.indexOf(term.term_arabic);
              if (index !== -1) {
                const scalarStart = Array.from(text.slice(0,index)).length;
                matches.push({
                  start: scalarStart,
                  end: scalarStart + Array.from(term.term_arabic as string).length,
                  termId: term.id,
                  termText: term.term_arabic
                });
              }
            }
          });

          if(active)setTermMatches(matches);
        }
      } catch (error) {
        console.error('Ошибка загрузки терминов:', error);
      }
    };

    loadTermMatches();
    return () => {active = false;};
  }, [text, showTooltips, token]);

  // Обработка клика по букве
  const handleLetterClick = useCallback((index: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Вызываем callback при клике на любую букву
    // AyahSearchStep сам решит, правильная это буква или нет
    onLetterClick(index);
  }, [onLetterClick]);

  // Обработка наведения на букву
  const handleLetterHover = useCallback((index: number, event: React.MouseEvent) => {
    if (showTooltips && token) {
      setTooltipPosition({
        x: event.clientX,
        y: event.clientY
      });
    }
  }, [showTooltips, token]);

  // Обработка ухода мыши
  const handleLetterLeave = useCallback(() => {
    setTooltipPosition(undefined);
  }, []);

  // Проверка, является ли позиция частью термина
  const getTermForPosition = (index: number): TermMatch | null => {
    return termMatches.find(match => index >= match.start && index < match.end) || null;
  };

  // Рендеринг текста с подсветкой
  const renderText = () => {
    const elements: React.ReactNode[] = [];
    const characters = Array.from(text);
    let currentIndex = 0;

    while (currentIndex < characters.length) {
      const char = characters[currentIndex];
      const isTargetLetter = char === targetLetter;
      const isFound = foundPositions.includes(currentIndex);
      const termMatch = getTermForPosition(currentIndex);

      // Определяем стили для символа
      let backgroundColor = 'transparent';
      let color = 'inherit';
      let cursor = 'pointer'; // Все символы кликабельны для поиска
      let fontWeight = 'normal';

      if (isTargetLetter && isFound) {
        // Только найденные буквы подсвечиваются зелёным
        backgroundColor = '#4caf50';
        color = 'white';
        fontWeight = 'bold';
      } else if (termMatch) {
        backgroundColor = '#e3f2fd'; // Голубой для терминов
        color = '#1976d2';
      }
      // Целевые буквы НЕ подсвечиваются до клика — это задача пользователя их найти

      // Создаем элемент символа
      const charElement = (
        <Box
          key={currentIndex}
          component="span"
          sx={{
            display: 'inline-block',
            padding: '2px 4px',
            margin: '1px',
            borderRadius: '4px',
            backgroundColor,
            color,
            cursor,
            fontWeight,
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              zIndex: 1,
              position: 'relative'
            }
          }}
          onClick={handleLetterClick.bind(null, currentIndex)}
          onMouseEnter={handleLetterHover.bind(null, currentIndex)}
          onMouseLeave={handleLetterLeave}
        >
          {char}
        </Box>
      );

      // Если это термина, оборачиваем в TermTooltip
      if (termMatch && showTooltips && token) {
        elements.push(
          <TermTooltip
            key={`tooltip-${currentIndex}`}
            termId={termMatch.termId}
            termText={termMatch.termText}
            token={token}
            position={tooltipPosition}
            onClose={() => {
              setTooltipPosition(undefined);
            }}
          >
            {charElement}
          </TermTooltip>
        );
      } else {
        elements.push(charElement);
      }

      currentIndex++;
    }

    return elements;
  };

  return (
    <Box
      sx={{
        direction,
        textAlign: direction === 'rtl' ? 'right' : 'left',
        lineHeight: 2,
        userSelect: 'none'
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontFamily,
          fontSize,
          direction,
          wordBreak: 'break-word',
          hyphens: 'auto'
        }}
      >
        {renderText()}
      </Typography>

      {/* Легенда */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Кликните на буквы "{targetLetter}" чтобы их найти
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: '#4caf50',
              borderRadius: '4px'
            }}
          />
          <Typography variant="caption">
            Найденная буква
          </Typography>
        </Box>

        {showTooltips && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                border: '1px solid #1976d2'
              }}
            />
            <Typography variant="caption">
              Термин (кликните для информации)
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HighlightedText;
